import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/hooks/useAppSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LimaClock } from "@/components/LimaClock";
import { PageLoader } from "@/components/PageLoader";
import { toast } from "sonner";
import { Music2, Loader2, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().min(1, "Ingresa tu email").email("Ingresa un email válido").max(255),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres").max(100)
});

// Apple warning now shown to all users

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, user } = useAuth();
  const { settings, isLoading } = useAppSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  if (isLoading) return <PageLoader />;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validatedData = loginSchema.parse({ email, password });
      setIsSubmitting(true);
      const { error } = await signIn(validatedData.email, validatedData.password);
      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Credenciales inválidas. Verifica tu email y contraseña.");
        } else {
          toast.error(error.message);
        }
        return;
      }
      toast.success("¡Bienvenido de nuevo! 🎵", { description: "Nos alegra verte otra vez" });
      navigate("/dashboard");
    } catch (err) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error("Error al iniciar sesión");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main
      className="relative flex min-h-screen flex-col items-center justify-center p-4"
      style={{
        backgroundImage: settings.appCoverImage
          ? `var(--cover-overlay), url(${settings.appCoverImage})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {!settings.appCoverImage && <div className="absolute inset-0 bg-gradient-hero" />}

      {settings.loginClockEnabled && (
        <div className="relative z-10 mb-4">
          <LimaClock size={settings.loginClockSize} />
        </div>
      )}

      <Card className="relative w-full max-w-md border-border/50 bg-card/80 backdrop-blur-sm animate-scale-in">
        {/* Theme + Apple buttons top-right */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-yellow-500" aria-label="Aviso para usuarios Apple">
                <AlertTriangle className="h-4 w-4" />
                <span className="sr-only">Aviso para usuarios Apple</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className="w-64 p-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">⚠️ Dispositivo Apple</span><br />
                Para la mejor experiencia con quizzes, te recomendamos usar una computadora.
              </p>
            </PopoverContent>
          </Popover>
          <ThemeToggle />
        </div>
        <CardHeader className="space-y-3 text-center pb-4">
          {settings.appLogo ? (
            <img src={settings.appLogo} alt="Logo" className="mx-auto h-12 w-12 rounded-full object-cover" width={48} height={48} fetchPriority="high" decoding="async" />
          ) : (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-primary">
              <Music2 className="h-6 w-6 text-primary-foreground" />
            </div>
          )}
          <div>
            <CardTitle className="text-2xl font-bold">{settings.appName}</CardTitle>
            <CardDescription className="mt-1 text-sm">
              Ingresa tus credenciales para acceder
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleLogin} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="login-email">Email</Label>
              <Input id="login-email" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} required disabled={isSubmitting} className="bg-background/50" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password">Contraseña</Label>
              <Input id="login-password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required disabled={isSubmitting} className="bg-background/50" />
            </div>
            <Button type="submit" className="w-full bg-gradient-primary hover:opacity-90 transition-opacity" disabled={isSubmitting}>
              {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Iniciando sesión...</>) : "Iniciar Sesión"}
            </Button>
          </form>

        </CardContent>
      </Card>
    </main>
  );
};

export default Login;
